import { useEffect, useState, type FormEvent } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { FormField, FormSelect } from '../shared/form';
import { useCreateModelProvider } from '../../hooks/useModels';
import type { CreateModelProviderInput, ModelProviderAdapter } from '../../types';
import { CatalogDialog, PRIMARY_BUTTON, SECONDARY_BUTTON } from './CatalogDialog';
import { catalogFormErrors, type CatalogFormErrors } from './catalogErrors';

/**
 * Add provider (task 1102). Creates a Model Provider record through
 * `POST /api/model-providers` (service task 1101). Every provider speaks one of
 * three protocols; an OpenAI-compatible one needs its endpoint, the other two
 * take an optional Base URL override under Advanced. The API key is write-only:
 * it is sent once here and the service keeps only its last four for display.
 */

export const PROTOCOL_OPTIONS: { value: ModelProviderAdapter; label: string }[] = [
  { value: 'anthropic', label: 'Anthropic' },
  { value: 'openai', label: 'OpenAI' },
  { value: 'openai-compatible', label: 'OpenAI-compatible' },
];

/**
 * Lowercase letters and digits separated by single hyphens, at most 64
 * characters: the same rule the service applies to the key Model rows and
 * account policies reference.
 */
export const PROVIDER_KEY_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const PROVIDER_KEY_MAX_LENGTH = 64;

/** The service refuses an API key shorter than this. */
export const API_KEY_MIN_LENGTH = 8;

const EMPTY_ERRORS: CatalogFormErrors = { fields: {}, general: null };

interface AddModelProviderDialogProps {
  open: boolean;
  onClose: () => void;
}

export function AddModelProviderDialog({ open, onClose }: AddModelProviderDialogProps) {
  const create = useCreateModelProvider();
  const [displayName, setDisplayName] = useState('');
  const [key, setKey] = useState('');
  const [adapter, setAdapter] = useState<ModelProviderAdapter>('anthropic');
  const [baseUrl, setBaseUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [errors, setErrors] = useState<CatalogFormErrors>(EMPTY_ERRORS);

  // A fresh dialog every time it opens: no key or half-typed form carries over.
  useEffect(() => {
    if (!open) return;
    setDisplayName('');
    setKey('');
    setAdapter('anthropic');
    setBaseUrl('');
    setApiKey('');
    setEnabled(true);
    setAdvancedOpen(false);
    setErrors(EMPTY_ERRORS);
    create.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const baseUrlRequired = adapter === 'openai-compatible';
  const showBaseUrl = baseUrlRequired || advancedOpen;

  const validate = (): Record<string, string> => {
    const fields: Record<string, string> = {};
    if (!displayName.trim()) fields.displayName = 'Display name is required';
    if (!key.trim()) fields.key = 'Key is required';
    else if (key.trim().length > PROVIDER_KEY_MAX_LENGTH || !PROVIDER_KEY_PATTERN.test(key.trim())) {
      fields.key = 'Use lowercase letters and numbers separated by single hyphens, at most 64 characters';
    }
    if (baseUrlRequired && !baseUrl.trim()) fields.baseUrl = 'Base URL is required for an OpenAI-compatible provider';
    if (!apiKey.trim()) fields.apiKey = 'API key is required';
    else if (apiKey.trim().length < API_KEY_MIN_LENGTH) fields.apiKey = `API key must be at least ${API_KEY_MIN_LENGTH} characters`;
    return fields;
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const fields = validate();
    if (Object.keys(fields).length > 0) {
      setErrors({ fields, general: null });
      return;
    }
    setErrors(EMPTY_ERRORS);
    const payload: CreateModelProviderInput = {
      displayName: displayName.trim(),
      key: key.trim(),
      adapter,
      ...(showBaseUrl && baseUrl.trim() ? { baseUrl: baseUrl.trim() } : {}),
      apiKey,
      enabled,
    };
    try {
      await create.mutateAsync(payload);
      onClose();
    } catch (error) {
      setErrors(catalogFormErrors(error, 'key'));
    }
  };

  return (
    <CatalogDialog
      open={open}
      onClose={onClose}
      busy={create.isPending}
      title="Add provider"
      description="A Model Provider the platform calls with its own API key."
      testId="add-provider-dialog"
    >
      <form onSubmit={handleSubmit} noValidate className="space-y-4">
        <FormField
          label="Display name"
          name="provider-display-name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          error={errors.fields.displayName}
          required
          autoFocus
        />
        <div>
          <FormField
            label="Key"
            name="provider-key"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            error={errors.fields.key}
            placeholder="e.g. groq"
            required
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Immutable after creation</p>
        </div>
        <FormSelect
          label="Protocol"
          name="provider-protocol"
          value={adapter}
          onChange={(e) => setAdapter(e.target.value as ModelProviderAdapter)}
          options={PROTOCOL_OPTIONS}
          error={errors.fields.adapter}
          required
        />
        {!baseUrlRequired && (
          <button
            type="button"
            onClick={() => setAdvancedOpen((prev) => !prev)}
            aria-expanded={advancedOpen}
            className="inline-flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-700 dark:text-indigo-400"
          >
            {advancedOpen ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
            Advanced
          </button>
        )}
        {showBaseUrl && (
          <div>
            <FormField
              label="Base URL"
              name="provider-base-url"
              type="url"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              error={errors.fields.baseUrl}
              placeholder="https://api.example.com/v1"
              required={baseUrlRequired}
            />
            {!baseUrlRequired && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Optional. Overrides the protocol&apos;s default endpoint.
              </p>
            )}
          </div>
        )}
        <FormField
          label="API key"
          name="provider-api-key"
          type="password"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          error={errors.fields.apiKey}
          required
        />
        <div className="flex items-center justify-between">
          <span id="provider-enabled-label" className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Enabled
          </span>
          <EnabledToggle checked={enabled} onChange={setEnabled} labelledBy="provider-enabled-label" />
        </div>

        {errors.general && (
          <p role="alert" className="text-sm text-red-600 dark:text-red-400">
            {errors.general}
          </p>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} disabled={create.isPending} className={SECONDARY_BUTTON}>
            Cancel
          </button>
          <button type="submit" disabled={create.isPending} className={PRIMARY_BUTTON}>
            {create.isPending ? 'Adding…' : 'Add provider'}
          </button>
        </div>
      </form>
    </CatalogDialog>
  );
}

/** On/off switch, the same control the Preferences page uses. */
function EnabledToggle({
  checked,
  onChange,
  labelledBy,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  labelledBy: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-labelledby={labelledBy}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
        checked ? 'bg-indigo-500' : 'bg-zinc-300 dark:bg-zinc-600'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ease-in-out ${
          checked ? 'translate-x-4' : 'translate-x-0'
        }`}
      />
    </button>
  );
}
